import { create } from 'zustand'
import { useAuthStore } from '@/app/store/auth'
import { useLockStore } from '@/app/store/lock'
import {
    normalizePrefs,
    setTickerAccessHandlers,
    type NotificationRow,
    type TickerKind,
    type TickerNewEvent,
    type TickerPrefs,
} from '@/features/ticker/api'
import { levelAdmits } from '@/features/ticker/kinds'
import { userCanViewTicker } from '@/features/ticker/permission'

/**
 * The Recovery Ticker's client state (docs/ticker-design.md §9).
 *
 * Ephemeral, like chatNotifications: nothing here is written to localStorage. The preferences
 * live on the server per user (GET/PUT /api/me/preferences/ticker) and are mirrored here so the
 * socket and the conveyor can read them synchronously. The cursor and the queue are per page
 * load: a reload starts from "now" rather than replaying what the last tab already showed.
 *
 * THE ORDERING RULE. `ticker:new` is notice, not the record, and a socket drops messages whenever
 * it is down. So on every connect, reconnect or change of watched sets, the client backfills
 * from REST BEFORE it trusts the stream (§3.2):
 *   - beginSync() turns buffering on: live events that arrive while the backfill is in flight
 *     are held in `pending`, not shown;
 *   - completeSync() merges the backfill with the buffer, sorts by id, and ingests each row once.
 * Dedupe is by id (`seen`), so the -50 overlap the backfill asks for, and a live event that is
 * also in the backfill, are each shown exactly once.
 *
 * Per set, `floors[setId]` is "now" as it was when the set was first watched. Nothing at or
 * below it is news, so choosing a set does not replay its history onto the conveyor.
 *
 * THE BACKLOG RULE. The conveyor shows the news, not a replay. When more than BACKLOG_MAX
 * messages are waiting for it - after a reconnect, a hidden tab, or a burst - they are folded
 * into ONE card ("N messages while you were away · See all") that opens the panel, which holds
 * every one of them anyway.
 */

export interface TickerItem {
    id: number
    setId: number
    kind: TickerKind
    title: string
    summary: string
    initials: string | null
    createdAt: string
    /** A development sample ("Populate ticker"); the card says so. */
    sample?: boolean
}

/** The card that stands for messages folded out of the queue. Its id is negative, never a row's. */
export interface TickerFoldCard {
    id: number
    fold: true
    count: number
    /** 'away': they arrived while this client was away (reconnect, hidden tab); 'more': a burst. */
    reason: 'away' | 'more'
}

export type TickerCard = TickerItem | TickerFoldCard

export const isFoldCard = (card: TickerCard): card is TickerFoldCard => 'fold' in card

export interface TickerPanelState {
    open: boolean
    /** The message a clicked card opened the panel on: its row opens expanded and highlighted. */
    focusId: number | null
}

interface TickerFold {
    count: number
    reason: 'away' | 'more'
}

/**
 * Why the ticker is not receiving, as a code the dropdown can say in words:
 *   - 'unavailable': the watch or its backfill failed, or was refused for a passing reason; it is
 *     being retried;
 *   - 'unauthenticated': the server no longer accepts this sign-in on the socket or the REST reads;
 *   - 'invalid': the server rejected what the client asked for; retried on the next change or
 *     reconnect;
 *   - 'forbidden' / 'revoked': refused the permission (the ticker is hidden, so never shown).
 */
export type TickerTrouble = 'unavailable' | 'unauthenticated' | 'invalid' | 'forbidden' | 'revoked'

/**
 * Who hid the ticker:
 *   - 'refused': the socket server read the role and said no (a watch refused 'forbidden', or
 *     `ticker:revoked`). It stands until /auth/me grants ticker.view again (a new sign-in, an
 *     unlock, a refetch) or the user changes.
 *   - 'request': a REST read answered 403. That is also what the server's role read answers when it
 *     cannot read the role at all, so it is not a verdict: the next successful ticker read clears
 *     it, and it is re-asked now and then (connection.ts, recheckTickerAccess).
 */
export type TickerForbiddenBy = 'refused' | 'request'

interface TickerState {
    prefs: TickerPrefs | null
    /** The highest id processed. The backfill cursor is lastSeenId - 50. */
    lastSeenId: number
    /**
     * The highest notification id this page has met by ANY route (a prime, a backfill row, a live
     * event, whatever its set). Ids are global and committed in order (§3.2), so this is a lower
     * bound on "now" for every set, including one with no messages of its own.
     */
    highestKnownId: number
    floors: Record<number, number>
    seen: Set<number>
    /** What the conveyor has still to show, oldest first. Only kinds the level admits. */
    queue: TickerItem[]
    /** Messages folded into one summary card, shown before `queue`; null when there are none. */
    fold: TickerFold | null
    /** Admitted messages since the dropdown was last opened. */
    unseen: number
    menuOpen: boolean
    panel: TickerPanelState
    syncing: boolean
    syncGen: number
    pending: TickerItem[]
    /** Why the ticker is not receiving just now; null once a sync has succeeded. */
    watchError: TickerTrouble | null
    /**
     * The server has refused this user the ticker (a 403, or a watch refused for the permission).
     * The ticker hides and stops asking. `forbiddenBy` says what may bring it back.
     */
    forbidden: boolean
    forbiddenBy: TickerForbiddenBy | null
    /**
     * Bumped when stored messages were deleted from this browser ("Clear sample messages"): the
     * history panel's snapshot is frozen on purpose, so this is what tells it to take a new one.
     */
    historyEpoch: number
}

/** A backfill that overflowed: the rows were skipped and the cursor jumps to the live edge. */
export interface TickerCatchUp {
    /** The newest id in the watched sets when the jump was made. */
    newestId: number
    /** How many messages the jump skipped (the panel still holds them). */
    missed: number
}

interface TickerActions {
    setPrefs: (prefs: TickerPrefs) => void
    setFloors: (setIds: readonly number[], floor: number) => void
    /** Start buffering live events; returns this sync's generation. */
    beginSync: () => number
    /** Merge a finished backfill with the buffer. Ignored if a newer sync has started. */
    completeSync: (gen: number, rows: TickerItem[], catchUp?: TickerCatchUp) => boolean
    /** The watch was refused: stop buffering and drop what was held. */
    failSync: (gen: number, error: TickerTrouble) => void
    /** This sync hit trouble and will be retried: say so, but keep buffering. */
    noteTrouble: (gen: number, error: TickerTrouble) => void
    /** Nothing is watched: retire any sync in flight and drop the buffer. */
    stopSync: () => void
    receiveLive: (event: TickerNewEvent) => void
    /** Take the next card for the conveyor: the fold card first, then the queue. */
    shift: () => TickerCard | undefined
    clearUnseen: () => void
    setMenuOpen: (open: boolean) => void
    openPanel: (focusId?: number | null) => void
    closePanel: () => void
    /** The server refused the ticker: hide it until `clearForbidden` (see TickerForbiddenBy). */
    markForbidden: (by?: TickerForbiddenBy) => void
    /**
     * Show the ticker again. 'request' clears only a refusal that came from a REST 403 (a later
     * read succeeded); 'any' clears either kind (/auth/me grants ticker.view again).
     */
    clearForbidden: (scope: 'request' | 'any') => void
    /**
     * A set's sample messages were deleted: take them off the conveyor's queue and the buffer, and
     * have the history panel read again. A card already on the bar finishes its dwell.
     */
    samplesCleared: (setId: number) => void
    /** Forget the cursor, floors, queue and buffer; keep the preferences, the panel and a refusal. */
    resetSession: () => void
    /** Forget everything: the session's user is gone. */
    reset: () => void
}

export type TickerStore = TickerState & TickerActions

/** Past this, ids far below the backfill window are forgotten; they can never come back. */
const SEEN_CAP = 5000
const SEEN_KEEP_BELOW_CURSOR = 1000
/** More than this many cards waiting and they are folded into one summary card. */
export const BACKLOG_MAX = 5

export const rowToItem = (row: NotificationRow): TickerItem => ({
    id: row.id,
    setId: row.recovery_set_id,
    kind: row.kind,
    title: row.title,
    summary: row.summary,
    initials: row.author?.initials ?? null,
    createdAt: row.created_at,
    ...(row.sample ? { sample: true } : {}),
})

export const eventToItem = (e: TickerNewEvent): TickerItem => ({
    id: e.id,
    setId: e.recovery_set_id,
    kind: e.kind,
    title: e.title,
    summary: e.summary,
    initials: e.initials ?? null,
    createdAt: e.created_at,
    ...(e.sample ? { sample: true } : {}),
})

const initialState = (): TickerState => ({
    prefs: null,
    lastSeenId: 0,
    highestKnownId: 0,
    floors: {},
    seen: new Set(),
    queue: [],
    fold: null,
    unseen: 0,
    menuOpen: false,
    panel: { open: false, focusId: null },
    syncing: false,
    syncGen: 0,
    pending: [],
    watchError: null,
    forbidden: false,
    forbiddenBy: null,
    historyEpoch: 0,
})

/** Add `count` messages to the fold. 'away' wins: the card says why most of them are there. */
function addToFold(fold: TickerFold | null, count: number, reason: TickerFold['reason']): TickerFold | null {
    if (count <= 0) return fold
    return { count: (fold?.count ?? 0) + count, reason: fold?.reason === 'away' || reason === 'away' ? 'away' : 'more' }
}

const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden'

/**
 * Ingest rows in id order against a working copy of the state. Returns the changed fields.
 * A row is shown when the ticker is on, its set is watched, it is above that set's floor, it has
 * not been seen, and the level admits its kind. `reason` says why a queue past BACKLOG_MAX is
 * folded: 'away' after a reconnect, otherwise 'more' (or 'away' while the tab is hidden).
 */
function ingest(state: TickerState, rows: TickerItem[], reason?: TickerFold['reason']): Partial<TickerState> {
    const prefs = state.prefs
    if (rows.length === 0) return {}
    const highestKnownId = rows.reduce((max, row) => Math.max(max, row.id), state.highestKnownId)
    // Refused the ticker: a stray event still in flight is not shown.
    if (!prefs || !prefs.enabled || state.forbidden) return { highestKnownId }
    const watched = new Set(prefs.recoverySetIds)
    const seen = new Set(state.seen)
    let queue = state.queue.slice()
    let { lastSeenId, unseen, fold } = state
    const sorted = rows.slice().sort((a, b) => a.id - b.id)
    for (const row of sorted) {
        if (!watched.has(row.setId)) continue
        if (row.id <= (state.floors[row.setId] ?? 0)) continue
        if (seen.has(row.id)) continue
        seen.add(row.id)
        if (row.id > lastSeenId) lastSeenId = row.id
        if (!levelAdmits(prefs.level, row.kind)) continue
        queue.push(row)
        if (!state.menuOpen) unseen += 1
    }
    // A backlog is never replayed card by card.
    if (queue.length > BACKLOG_MAX) {
        fold = addToFold(fold, queue.length, reason ?? (tabHidden() ? 'away' : 'more'))
        queue = []
    }
    if (seen.size > SEEN_CAP) {
        for (const id of seen) if (id < lastSeenId - SEEN_KEEP_BELOW_CURSOR) seen.delete(id)
    }
    return { seen, queue, fold, lastSeenId, unseen, highestKnownId }
}

let foldSeq = 0

export const useTickerStore = create<TickerStore>((set, get) => ({
    ...initialState(),

    setPrefs: (prefs) =>
        set((state) => {
            const whole = normalizePrefs(prefs)
            const next: TickerPrefs = {
                ...whole,
                recoverySetIds: Array.from(new Set(whole.recoverySetIds)).sort((a, b) => a - b),
            }
            // Switched off, every set gives up its floor, so switching back on primes each at
            // "now" and does not replay what was said while the ticker was off.
            if (!next.enabled) return { prefs: next, queue: [], fold: null, pending: [], floors: {} }
            // A set that is no longer watched gives up its floor, so choosing it again later
            // starts from "now" and does not replay what it said while unwatched.
            const watched = new Set(next.recoverySetIds)
            const floors: Record<number, number> = {}
            for (const [id, floor] of Object.entries(state.floors)) if (watched.has(Number(id))) floors[Number(id)] = floor
            const queue = state.queue.filter((item) => watched.has(item.setId) && levelAdmits(next.level, item.kind))
            return { prefs: next, floors, queue }
        }),

    setFloors: (setIds, floor) =>
        set((state) => {
            const floors = { ...state.floors }
            for (const id of setIds) floors[id] = floor
            return { floors, highestKnownId: Math.max(state.highestKnownId, floor) }
        }),

    // The last sync's trouble stays on show while this one runs: a retry that fails again must not
    // make the dropdown's line blink. A success (completeSync) or nothing to watch (stopSync) clears it.
    beginSync: () => {
        const gen = get().syncGen + 1
        set({ syncing: true, syncGen: gen })
        return gen
    },

    completeSync: (gen, rows, catchUp) => {
        const state = get()
        if (gen !== state.syncGen) return false
        if (!catchUp) {
            set({ ...ingest(state, [...rows, ...state.pending], 'away'), pending: [], syncing: false, watchError: null })
            return true
        }
        // The backlog was too long to read: jump every watched set to the live edge. The skipped
        // messages (and whatever was still queued from before) become the fold card's count.
        // Nothing at or below the edge is news any more, so a later reconnect's overlap cannot
        // bring a skipped message back onto the conveyor.
        const edge = Math.max(catchUp.newestId, state.lastSeenId)
        const floors = { ...state.floors }
        for (const id of state.prefs?.recoverySetIds ?? []) floors[id] = Math.max(floors[id] ?? 0, edge)
        const jump: Partial<TickerState> = {
            floors,
            lastSeenId: edge,
            highestKnownId: Math.max(state.highestKnownId, edge),
            queue: [],
            fold: addToFold(state.fold, catchUp.missed + state.queue.length, 'away'),
            unseen: state.menuOpen ? state.unseen : state.unseen + catchUp.missed,
        }
        // Live events held during the sync that are newer than the edge are still news.
        set({ ...jump, ...ingest({ ...state, ...jump }, state.pending, 'away'), pending: [], syncing: false, watchError: null })
        return true
    },

    failSync: (gen, error) => {
        if (gen !== get().syncGen) return
        set({ syncing: false, pending: [], watchError: error })
    },

    noteTrouble: (gen, error) => {
        if (gen !== get().syncGen || get().watchError === error) return
        set({ watchError: error })
    },

    stopSync: () => set((state) => ({ syncGen: state.syncGen + 1, syncing: false, pending: [], watchError: null })),

    receiveLive: (event) => {
        const item = eventToItem(event)
        const state = get()
        if (state.syncing) {
            if (!state.pending.some((p) => p.id === item.id)) set({ pending: [...state.pending, item] })
            return
        }
        const changes = ingest(state, [item])
        if (Object.keys(changes).length) set(changes)
    },

    shift: () => {
        const { fold, queue } = get()
        if (fold) {
            set({ fold: null })
            return { id: -++foldSeq, fold: true, count: fold.count, reason: fold.reason }
        }
        const [head, ...rest] = queue
        if (head) set({ queue: rest })
        return head
    },

    clearUnseen: () => set({ unseen: 0 }),

    setMenuOpen: (menuOpen) => set(menuOpen ? { menuOpen, unseen: 0 } : { menuOpen }),

    openPanel: (focusId = null) => set({ panel: { open: true, focusId: focusId ?? null } }),

    closePanel: () => set({ panel: { open: false, focusId: null } }),

    markForbidden: (by = 'refused') =>
        set((state) => {
            // A refusal the server read the role for outranks a 403 that may have been a blip.
            if (state.forbidden) return state.forbiddenBy === 'refused' || by === 'request' ? {} : { forbiddenBy: by }
            return {
                forbidden: true,
                forbiddenBy: by,
                syncGen: state.syncGen + 1,
                syncing: false,
                pending: [],
                queue: [],
                fold: null,
                panel: { open: false, focusId: null },
            }
        }),

    clearForbidden: (scope) =>
        set((state) => {
            if (!state.forbidden) return {}
            if (scope === 'request' && state.forbiddenBy !== 'request') return {}
            return { forbidden: false, forbiddenBy: null, watchError: null }
        }),

    samplesCleared: (setId) =>
        set((state) => {
            const keep = (item: TickerItem) => !(item.sample && item.setId === setId)
            return { queue: state.queue.filter(keep), pending: state.pending.filter(keep), historyEpoch: state.historyEpoch + 1 }
        }),

    // The refusal survives: resetSession runs when the connection is torn down, which a refusal
    // itself causes, and forgetting it here would put the ticker straight back to be refused again.
    // What clears it is clearForbidden (a later successful read, /auth/me granting ticker.view) or
    // reset (the user changed).
    //
    // A hide caused by a REST 403 ('request') is provisional - it clears itself on the next good
    // read - so it keeps the reading position (cursor, floors, what was seen). When the ticker
    // returns, the catch-up then covers everything written while it was hidden, exactly as it does
    // after a plain disconnect. Forgetting it here made the return re-prime every set at the newest
    // id and silently skip that window, RTA alerts included (found in the final ticker review, 24 Sep).
    resetSession: () =>
        set((state) => ({
            ...initialState(),
            prefs: state.prefs,
            panel: state.panel,
            forbidden: state.forbidden,
            forbiddenBy: state.forbiddenBy,
            syncGen: state.syncGen + 1,
            ...(state.forbiddenBy === 'request'
                ? {
                      lastSeenId: state.lastSeenId,
                      highestKnownId: state.highestKnownId,
                      floors: state.floors,
                      seen: state.seen,
                      unseen: state.unseen,
                      fold: state.fold,
                  }
                : {}),
        })),

    reset: () => set((state) => ({ ...initialState(), syncGen: state.syncGen + 1 })),
}))

// A 403 from any ticker endpoint hides the ticker; a later success from any of them brings it
// back, because the server's role read also answers 403 when it could not read the role at all.
setTickerAccessHandlers({
    onForbidden: () => useTickerStore.getState().markForbidden('request'),
    onAllowed: () => useTickerStore.getState().clearForbidden('request'),
})

// The ticker's state belongs to the signed-in user. When that user signs out, or a different one
// signs in in the same tab, everything goes: the preferences, the queue, the panel, a refusal.
// When the same user's /auth/me arrives again (a sign-in, an unlock, a refetch) and still grants
// ticker.view, a refusal is lifted: the next watch asks the server again, and it decides.
useAuthStore.subscribe((state, prev) => {
    const was = prev.user?.id ?? null
    const now = state.user?.id ?? null
    if (was != null && was !== now) {
        useTickerStore.getState().reset()
        return
    }
    if (state.user !== prev.user && userCanViewTicker(state.user)) useTickerStore.getState().clearForbidden('any')
})

// The lock screen sits above the app, which stays mounted beneath it. Nothing of the ticker's may
// hold the focus there: the history panel and the dropdown each trap it, and the password typed
// to unlock would go into them. Locking closes both.
useLockStore.subscribe((state, prev) => {
    if (!state.locked || prev.locked) return
    const ticker = useTickerStore.getState()
    if (ticker.panel.open) ticker.closePanel()
    if (ticker.menuOpen) ticker.setMenuOpen(false)
})
